import i18n from '@/i18n/i18n'
import FinancingCostSummaryService from '@/services/admin/FinancingCostSummaryService.js'
import country from '@/utilities/country'
import { build_query, build_query_with_prefix, errorMessage } from '@/utilities/helpers'

import { FINANCING_COST_SUMMARY_ERROR_MESSAGES } from '@/constants/errorMessages'

const { t } = i18n.global

export const allowedFilters = [
  'promissory_note_reason',
  'bank_id',
  'credit_start_date_between',
  'credit_code',
  'by_order_id',
  'payment_status'
]

export const allowedIncludes = 'financingBank,propertyOptions,financingCosts.order'

export const allowedSorts = ''

export const allowedAppends = ['planned_repayment_amount', 'realized_repayment_amount']

export const allowedActionsIncludes = 'details,user'

export const allowedActionsSorts = '-created_at'

export const namespaced = true

export const state = {
  financingCostSummary: {},
  financingCostSummaryActions: {},
  financingCostSummaries: [],
  meta: null,
  additionalMaturityDayList: [0, 1, 2],
  documents: []
}

export const mutations = {
  SET_FINANCING_COST_SUMMARIES(state, financingCostSummaries) {
    state.financingCostSummaries = financingCostSummaries.data
    state.meta = financingCostSummaries.meta
  },
  SET_FINANCING_COST_SUMMARY(state, financingCostSummary) {
    state.financingCostSummary = financingCostSummary
  },
  SET_FINANCING_COST_SUMMARY_ACTIONS(state, financingCostSummaryActions) {
    state.financingCostSummaryActions = financingCostSummaryActions.data
  },
  UPDATE_FINANCING_COST_SUMMARY(state, financingCostSummary) {
    state.financingCostSummary = financingCostSummary.data
  },
  SET_DOCUMENTS(state, documents) {
    state.documents = documents
  }
}

export const actions = {
  getFinancingCostSummaries({ commit, dispatch }, query) {
    let allowedAppendParams = allowedAppends

    if (country.is('ro')) {
      allowedAppendParams = []
    }

    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppendParams)

    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.getFinancingCostSummaries(newQuery)
        .then(response => {
          commit('SET_FINANCING_COST_SUMMARIES', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('views.layouts.financing_cost_summary')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancingCostSummary({ commit, getters, state }, id) {
    return new Promise(resolve => {
      FinancingCostSummaryService.getFinancingCostSummary(id).then(response => {
        commit('SET_FINANCING_COST_SUMMARY', response.data)
        resolve(response.data)
      })
    })
  },
  deleteFinancingCostSummary({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.deleteFinancingCostSummary(params.id)
        .then(response => {
          commit('SET_FINANCING_COST_SUMMARIES', response)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('views.layouts.financing_cost_summary')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('views.layouts.financing_cost_summary')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createFinancingCostSummary({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.createFinancingCostSummary(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.financing_cost_summary.add_import_financing_cost_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.financing_cost_summary.start_import_financing_cost_process_error')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  export({ dispatch }, id) {
    return new Promise(reject => {
      FinancingCostSummaryService.export(id)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('views.layouts.financing_cost_summary')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: FINANCING_COST_SUMMARY_ERROR_MESSAGES.exportError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancingCostSummaryActions({ commit, dispatch }, data) {
    let newQuery = build_query(data.query, '', allowedActionsIncludes, allowedActionsSorts)

    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.getFinancingCostSummaryActions(data.financingCostSummary, newQuery)
        .then(response => {
          commit('SET_FINANCING_COST_SUMMARY_ACTIONS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('views.layouts.financing_cost_summary')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  addFinancingCostSummaryAction({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.addFinancingCostSummaryAction(form.form, form.financing_cost_summary_id)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.financing_cost_summary.start_import_financing_cost_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
          location.reload()
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.admin.financing_cost_summary.start_import_financing_cost_process_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteFinancingCostSummaryAction({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.deleteFinancingCostSummaryAction(form.form, form.financing_cost_summary_id)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.financing_cost_summary.start_import_financing_cost_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
          location.reload()
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.admin.financing_cost_summary.start_import_financing_cost_process_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updatePaymentStatus({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.updatePaymentStatus(state.financingCostSummary.id, params)
        .then(response => {
          commit('UPDATE_FINANCING_COST_SUMMARY', response)
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('views.layouts.financing_cost_summary')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.update_error', {
                element: t('views.layouts.financing_cost_summary')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancingCostSummaryDocuments({ dispatch, commit, state }, id) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.getDocuments(id)
        .then(response => {
          commit('SET_DOCUMENTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadFinancingCostSummaryDocument({ dispatch, state }, data) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.uploadDocument(state.financingCostSummary.id, data)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewFinancingCostSummaryDocument({ dispatch, state }, document) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.viewDocument(state.financingCostSummary.id, document)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteFinancingCostSummaryDocument({ dispatch, state }, { documentId }) {
    return new Promise((resolve, reject) => {
      FinancingCostSummaryService.deleteDocument(state.financingCostSummary.id, documentId)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  exportFinancingCostSummaries({ dispatch }, query) {
    return new Promise(reject => {
      let newQuery = build_query_with_prefix(query, allowedFilters, 'financingCostSummary')

      FinancingCostSummaryService.exportFinancingCostSummaries(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('views.layouts.financing_cost_summary')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: FINANCING_COST_SUMMARY_ERROR_MESSAGES.multipleExportError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
